import { Editor, EditorProps, OnMount } from "@monaco-editor/react";
import { Box, useTheme } from "@mui/material";
import { SetStateAction, useEffect, useRef } from "react";
import type * as monaco from "monaco-editor";
import { format } from "sql-formatter";

interface MyEditorProps extends EditorProps {
  query: string;
  setQuery: React.Dispatch<SetStateAction<string>>;
  tables?: Record<string, { columnName: string; type: string }[]>;
  height?: string | number;
  panelResizeKey?: number;
  handleChangeGraph?: (key: string, value: string) => void;
}

const QueryEditor: React.FC<MyEditorProps> = ({
  query,
  setQuery,
  height,
  panelResizeKey,
  handleChangeGraph,
}) => {
  const theme = useTheme();
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();

  useEffect(() => {
    editorRef.current?.layout();
  }, [panelResizeKey]);

  const handleMount: OnMount = (editor, monacoInstance) => {
    editorRef.current = editor;

    monacoInstance.languages.registerDocumentFormattingEditProvider("sql", {
      provideDocumentFormattingEdits(model) {
        const formatted = format(model.getValue(), {
          language: "postgresql",
          tabWidth: 2,
          paramTypes: {
            custom: [{ regex: String.raw`\{\w+\}` }],
          },
        });
        return [
          {
            range: model.getFullModelRange(),
            text: formatted,
          },
        ];
      },
    });

    editor.getAction("editor.action.formatDocument")?.run();

    editor.layout();
  };

  return (
    <Editor
      key="monaco-editor"
      defaultLanguage="sql"
      height={height ?? "90%"}
      defaultValue={query}
      onChange={(v) => {
        setQuery(v ?? "");
        handleChangeGraph?.("query", v ?? "");
      }}
      theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
      options={{
        minimap: { enabled: false },
        formatOnPaste: true,
        formatOnType: true,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        fontSize: 13,
      }}
      onMount={handleMount}
    />
  );
};
export default QueryEditor;
